"use client";

import { useState } from "react";

// ─── Copy button ───────────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className="shrink-0 text-[10px] font-medium px-2.5 py-1 rounded-lg border border-border hover:border-borderStrong text-textSecondary hover:text-textPrimary transition-colors"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

// ─── Code block ────────────────────────────────────────────────────────────────
function CodeBlock({ value, label }: { value: string; label?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface overflow-hidden">
      {label && (
        <div className="px-3 py-1.5 border-b border-border text-[10px] text-textMuted font-medium uppercase tracking-widest">
          {label}
        </div>
      )}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <code className="flex-1 text-xs text-primary font-mono break-all">{value}</code>
        <CopyButton text={value} />
      </div>
    </div>
  );
}

// ─── Multi-line code block ─────────────────────────────────────────────────────
function CodeMultiBlock({ value, label }: { value: string; label?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface overflow-hidden">
      {label && (
        <div className="px-3 py-1.5 border-b border-border flex items-center justify-between">
          <span className="text-[10px] text-textMuted font-medium uppercase tracking-widest">
            {label}
          </span>
          <CopyButton text={value} />
        </div>
      )}
      <pre className="px-3 py-3 text-[11px] font-mono text-textSecondary overflow-x-auto leading-relaxed">
        {value}
      </pre>
    </div>
  );
}

// ─── Section header ────────────────────────────────────────────────────────────
function SectionHeader({
  icon,
  title,
  subtitle,
  badge,
}: {
  icon: string;
  title: string;
  subtitle: string;
  badge?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-2xl mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-semibold text-textPrimary">{title}</h2>
          {badge && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-primary/40 text-primary bg-primary/10">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-textSecondary mt-0.5">{subtitle}</p>
      </div>
    </div>
  );
}

// ─── Step ──────────────────────────────────────────────────────────────────────
function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-5 h-5 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center mt-0.5">
        <span className="text-[10px] font-bold text-primary">{number}</span>
      </div>
      <div className="flex-1 space-y-2">
        <p className="text-xs font-medium text-textPrimary">{title}</p>
        {children}
      </div>
    </div>
  );
}

// ─── Tool pill ─────────────────────────────────────────────────────────────────
function ToolPill({ name, desc }: { name: string; desc: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3 py-2.5 space-y-0.5">
      <p className="text-[11px] font-mono font-medium text-primary">{name}</p>
      <p className="text-[10px] text-textMuted">{desc}</p>
    </div>
  );
}

// ─── Callout ───────────────────────────────────────────────────────────────────
function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 px-3 py-2.5">
      {children}
    </div>
  );
}

// ─── Divider ───────────────────────────────────────────────────────────────────
function Divider() {
  return <div className="border-t border-border" />;
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function ConnectPage() {
  const cursorBrowseConfig = `{
  "mcpServers": {
    "reloadpi": {
      "url": "https://mcp.reloadpi.com/mcp"
    }
  }
}`;

  const npxConfig = `{
  "mcpServers": {
    "reloadpi": {
      "command": "npx",
      "args": ["-y", "reloadpi-mcp", "--stdio"],
      "env": {
        "EVM_PRIVATE_KEY": "0x..."
      }
    }
  }
}`;

  const cursorLocalConfig = `{
  "mcpServers": {
    "reloadpi": {
      "url": "http://localhost:3100/mcp"
    }
  }
}`;

  const selfHostSetup = `git clone https://github.com/cross555/reloadpi-mcp
cd reloadpi-mcp
npm install
cp .env.example .env   # add your EVM_PRIVATE_KEY
npm start`;

  const envExample = `EVM_PRIVATE_KEY=0x...   # funded Base wallet
PORT=3100              # optional, default 3100`;

  const claudePrompt = `Clone https://github.com/cross555/reloadpi-mcp, create a .env with my EVM_PRIVATE_KEY set to [your key], run npm install && npm start, then add it to my MCP config pointing to http://localhost:3100/mcp`;

  return (
    <div className="min-h-screen bg-background text-textPrimary">
      <div className="max-w-xl mx-auto px-4 py-8 space-y-8">

        {/* ── Hero ── */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-[10px] text-textSecondary font-medium uppercase tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Live on Base Mainnet
          </div>
          <h1 className="text-2xl font-semibold leading-snug">
            Connect Your AI to Reloadpi
          </h1>
          <p className="text-sm text-textSecondary leading-relaxed">
            Give your AI agent access to eSIMs, mobile top-ups, and gift vouchers across 190+ countries — paid in USDC on Base. Two ways to connect: MCP for Claude &amp; Cursor, or x402 for any autonomous agent.
          </p>
        </div>

        <Divider />

        {/* ── MCP ── */}
        <div className="space-y-4">
          <SectionHeader
            icon="🔌"
            title="Claude &amp; Cursor (MCP)"
            subtitle="Add Reloadpi as a tool server in any MCP-compatible AI client."
            badge="Recommended"
          />

          {/* Browse only */}
          <div className="space-y-3">
            <p className="text-[11px] font-medium uppercase tracking-widest text-textMuted">Browse only — no wallet needed</p>

            <Step number={1} title="Add the hosted MCP URL to your client">
              <CodeBlock
                label="MCP Server URL"
                value="https://mcp.reloadpi.com/mcp"
              />
              <p className="text-[11px] text-textSecondary">
                In Claude.ai: Settings → Integrations → Add MCP Server.{" "}
                In Cursor: <code className="text-primary font-mono">.cursor/mcp.json</code>
              </p>
            </Step>

            <Step number={2} title="Browse offers, check prices, poll orders">
              <p className="text-[11px] text-textSecondary">
                All browse and order-polling tools are free — no wallet required. Ask your AI to search for eSIMs, vouchers, or top-up plans.
              </p>
            </Step>
          </div>

          <Divider />

          {/* Purchases */}
          <div className="space-y-3">
            <p className="text-[11px] font-medium uppercase tracking-widest text-textMuted">Purchases — with your wallet</p>

            <p className="text-xs text-textSecondary leading-relaxed">
              Purchase tools require a funded Base wallet. Your AI pays directly from your wallet via x402. No account needed, no API key.
            </p>

            {/* Wallet setup */}
            <div className="rounded-xl border border-border bg-surface p-3 space-y-2.5">
              <p className="text-[11px] font-medium text-textPrimary">Setting up your wallet</p>
              <ol className="space-y-1.5 text-[11px] text-textSecondary leading-relaxed list-decimal list-inside">
                <li><span className="text-textPrimary font-medium">Use a dedicated wallet</span> — create a fresh EVM wallet just for the agent. The key lives in your config file, so don&apos;t reuse your main wallet.</li>
                <li><span className="text-textPrimary font-medium">Fund it with USDC on Base</span> — buy USDC on Coinbase and withdraw to the Base network, or bridge from another chain. No ETH needed — gas is covered by the x402 facilitator.</li>
                <li><span className="text-textPrimary font-medium">Add the key</span> to your MCP config&apos;s <code className="text-primary font-mono">env.EVM_PRIVATE_KEY</code> (below). It stays on your machine — never sent to Reloadpi.</li>
                <li><span className="text-textPrimary font-medium">Restart your client</span> — the purchase tools appear and the agent signs payments automatically.</li>
              </ol>
              <p className="text-[10px] text-textMuted">Keep the balance small — fund only what you want the agent to be able to spend.</p>
            </div>

            {/* npx — recommended */}
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <p className="text-[11px] font-medium text-primary">⚡ npx — easiest setup</p>
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full border border-primary/40 text-primary bg-primary/10">Recommended</span>
              </div>
              <p className="text-[11px] text-textSecondary">
                No cloning needed. Just paste this into your MCP config — npx downloads and starts the server automatically. Requires Node.js 18+.
              </p>
              <CodeMultiBlock label="claude_desktop_config.json / .cursor/mcp.json" value={npxConfig} />
            </div>

            {/* git clone — advanced */}
            <details className="group">
              <summary className="cursor-pointer text-[11px] font-medium text-textSecondary hover:text-textPrimary transition-colors list-none flex items-center gap-1.5">
                <span className="text-textMuted group-open:rotate-90 transition-transform inline-block">▶</span>
                Advanced — self-host via git clone
              </summary>
              <div className="mt-3 space-y-3 pl-4 border-l border-border">
                <Step number={1} title="Clone and run the MCP server locally">
                  <CodeMultiBlock label="terminal" value={selfHostSetup} />
                  <a
                    href="https://github.com/cross555/reloadpi-mcp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] text-primary hover:underline font-medium"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
                    </svg>
                    github.com/cross555/reloadpi-mcp
                  </a>
                </Step>

                <Step number={2} title="Add your wallet key to .env">
                  <CodeMultiBlock label=".env" value={envExample} />
                  <p className="text-[11px] text-textSecondary">
                    You need USDC on Base mainnet (chain ID 8453). Get it at{" "}
                    <a href="https://coinbase.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Coinbase</a>{" "}
                    or bridge from another chain.
                  </p>
                </Step>

                <Step number={3} title="Point your client to localhost">
                  <CodeMultiBlock label=".cursor/mcp.json" value={cursorLocalConfig} />
                  <p className="text-[11px] text-textSecondary">
                    In Claude.ai: Settings → Integrations → Add MCP Server → <code className="text-primary font-mono">http://localhost:3100/mcp</code>
                  </p>
                </Step>
              </div>
            </details>
          </div>

          {/* Claude prompt shortcut */}
          <Callout>
            <p className="text-[10px] font-medium text-primary mb-1.5">⚡ Let Claude set it up for you</p>
            <p className="text-[11px] text-textSecondary mb-2">
              Already using Claude Code or Cursor? Paste this prompt and your AI will clone the repo, create the <code className="text-primary font-mono">.env</code>, and start the server automatically:
            </p>
            <div className="rounded-lg border border-primary/20 bg-background/60 p-2.5 flex items-start gap-2">
              <p className="flex-1 text-[11px] font-mono text-textSecondary leading-relaxed break-all">{claudePrompt}</p>
              <CopyButton text={claudePrompt} />
            </div>
            <p className="text-[10px] text-textMuted mt-1.5">Takes under a minute. No account needed.</p>
          </Callout>

          {/* Quick reference */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-widest text-textMuted">Quick reference — config snippets</p>
            <div className="grid grid-cols-1 gap-2">
              <CodeMultiBlock label="Browse only (hosted URL)" value={cursorBrowseConfig} />
              <CodeMultiBlock label="Purchases via npx (recommended)" value={npxConfig} />
              <CodeMultiBlock label="Purchases via localhost (git clone)" value={cursorLocalConfig} />
            </div>
          </div>

          {/* Available tools */}
          <div className="space-y-2">
            <p className="text-[10px] font-medium uppercase tracking-widest text-textMuted">
              Available tools
            </p>

            <p className="text-[10px] text-textMuted font-medium">Vouchers</p>
            <div className="grid grid-cols-1 gap-1.5">
              <ToolPill name="browse_voucher_offers" desc="Search gift cards from 5000+ brands across 150+ countries. Filter by brand, country, or category." />
              <ToolPill name="get_voucher_offer"     desc="Get full details for a specific voucher — price, denomination, priceType, and required recipient fields." />
              <ToolPill name="get_voucher_filters"   desc="Get all valid filter values (brands, countries, categories) before calling browse." />
              <ToolPill name="purchase_voucher"      desc="Buy a voucher. Returns PIN, serial code, or redemption URL when available — email delivery for some brands." />
            </div>

            <p className="text-[10px] text-textMuted font-medium pt-1">Mobile Top-ups</p>
            <div className="grid grid-cols-1 gap-1.5">
              <ToolPill name="browse_topup_offers" desc="Find airtime and data top-up offers across 500+ operators in 150+ countries." />
              <ToolPill name="get_topup_offer"     desc="Get full details for a specific top-up offer — price, operator, country, required fields." />
              <ToolPill name="purchase_topup"      desc="Top up a mobile number (E.164 format). Airtime credited directly to recipient's SIM." />
            </div>

            <p className="text-[10px] text-textMuted font-medium pt-1">eSIMs</p>
            <div className="grid grid-cols-1 gap-1.5">
              <ToolPill name="browse_esim_offers" desc="Browse eSIM plans across 190+ countries — unlimited, regional, and global roaming plans." />
              <ToolPill name="get_esim_offer"     desc="Get full details for a specific eSIM plan — price, data, duration, coverage." />
              <ToolPill name="purchase_esim"      desc="Buy an eSIM. Returns ICCID and QR code. Poll get_order if QR is not immediately available." />
            </div>

            <p className="text-[10px] text-textMuted font-medium pt-1">Orders &amp; Refunds</p>
            <div className="grid grid-cols-1 gap-1.5">
              <ToolPill name="get_order"               desc="Poll fulfillment state and retrieve delivery payload (ICCID, QR, PIN, redeem URL). Free — no payment." />
              <ToolPill name="recover_order_by_txhash" desc="Recover a lost order using the on-chain txHash — if the purchase response was dropped mid-flight." />
              <ToolPill name="claim_refund"            desc="Manual refund trigger for failed/expired eSIM orders. Failed orders refund automatically within ~5 min — use this only as a fallback." />
            </div>
          </div>

          {/* Refund note */}
          <div className="rounded-xl border border-border bg-surface p-3 space-y-1">
            <p className="text-[10px] font-medium text-textPrimary">Refund policy</p>
            <p className="text-[11px] text-textSecondary">
              Failed eSIM and carrier-rejected top-up orders are refunded automatically in USDC to the agent wallet within ~5 minutes — no action needed. Vouchers are non-refundable once a code is issued.
            </p>
          </div>
        </div>

        <Divider />

        {/* ── x402 ── */}
        <div className="space-y-4">
          <SectionHeader
            icon="⚡"
            title="Autonomous Agents (x402)"
            subtitle="Zero-config access for any agent that speaks the x402 payment protocol."
          />

          <p className="text-xs text-textSecondary leading-relaxed">
            Reloadpi is a first-implementer of the{" "}
            <span className="text-textPrimary font-medium">x402-signals</span> spec. Any agent with a Base wallet can discover, browse, and purchase without API keys or pre-registration — the payment is the authentication.
          </p>

          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-surface p-3 space-y-2">
              <p className="text-[10px] font-medium uppercase tracking-widest text-textMuted">Discovery</p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-textMuted w-20 shrink-0">Manifest</span>
                  <code className="text-[11px] font-mono text-primary">https://api.reloadpi.com/.well-known/x402</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-textMuted w-20 shrink-0">Bazaar</span>
                  <a
                    href="https://agentic.market/?service=api-reloadpi-com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] font-mono text-primary hover:underline truncate"
                  >
                    agentic.market/reloadpi
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-textMuted w-20 shrink-0">Network</span>
                  <span className="text-[11px] text-textSecondary">Base mainnet · Chain ID 8453 · USDC</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {[
                { label: "Browse", price: "~$0.001–0.004" },
                { label: "Purchase", price: "Final price shown first" },
                { label: "Polling", price: "Free" },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-border bg-surface px-3 py-2.5 text-center space-y-1"
                >
                  <p className="text-[10px] font-medium text-textPrimary">{item.label}</p>
                  <p className="text-[9px] text-textMuted">{item.price}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Divider />

        {/* ── Partner API ── */}
        <div className="space-y-3">
          <SectionHeader
            icon="🔑"
            title="Partner API"
            subtitle="Traditional API key access — no crypto wallet required."
          />
          <p className="text-xs text-textSecondary leading-relaxed">
            Building a platform or integration that needs server-to-server access? Apply for a partner key and use the same catalog endpoints without x402 payments.
          </p>
          <a
            href="/partner/sdk"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface hover:border-borderStrong px-4 py-2.5 text-xs font-medium text-textPrimary transition-colors"
          >
            View Partner SDK docs →
          </a>
        </div>

        <Divider />

        {/* ── Footer note ── */}
        <p className="text-[11px] text-textMuted text-center pb-2">
          Reloadpi · Base mainnet · USDC payments ·{" "}
          <a href="/.well-known/x402" className="hover:text-textSecondary transition-colors">
            x402 manifest
          </a>{" "}
          ·{" "}
          <a href="/trust-security" className="hover:text-textSecondary transition-colors">
            Trust &amp; Security
          </a>{" "}
          ·{" "}
          <a
            href="https://github.com/cross555/reloadpi-mcp"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-textSecondary transition-colors"
          >
            GitHub
          </a>
        </p>

      </div>
    </div>
  );
}